import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { PreInviteScreen } from '@/routes/participants/_authenticated/projects/$projectId/-components/pre-invite-screen';
import { externalScreenerQueryOptions } from '@/routes/participants/_public/invite/-invite/queries';
import { publicProjectDetailsQueryOptions } from '@/routes/participants/_public/projects/$projectId/-public-apply/queries';

/** The participant may start (or return to) the task. */
const STARTABLE_STATUSES = new Set(['INVITED', 'SCHEDULED']);
/** The study is done for them. */
const DONE_STATUSES = new Set(['ATTENDED', 'PAID']);

/**
 * An admitted external participant's unmoderated study: the same "you're
 * invited, start the project" screen the participant app shows after an
 * instant invite. Starting asks core-api for the task link as this
 * project's session, and the survey's completion redirect brings them back
 * through `./complete`.
 */
export const Route = createFileRoute(
  '/participants/_external/external/projects/$projectId/study',
)({
  loader: async ({ params, context }) => {
    const userId = context.externalUserId;
    const [screener] = await Promise.all([
      context.queryClient.query(
        externalScreenerQueryOptions({ projectId: params.projectId, userId }),
      ),
      context.queryClient.query(
        publicProjectDetailsQueryOptions({ projectId: params.projectId }),
      ),
    ]);

    const status = screener.existingResponse?.status;
    if (status === 'DRAFT') {
      throw redirect({
        to: '/participants/external/projects/$projectId/screener',
        params: { projectId: params.projectId },
      });
    }
    if (status !== undefined && DONE_STATUSES.has(status)) {
      throw redirect({ to: '/participants/invite/done' });
    }
  },
  errorComponent: Unavailable,
  component: ExternalStudyRoute,
});

function ExternalStudyRoute() {
  const { projectId } = Route.useParams();
  const { externalUserId } = Route.useRouteContext();
  const { data: screener } = useQuery(
    externalScreenerQueryOptions({ projectId, userId: externalUserId }),
  );
  const { data: details } = useQuery(
    publicProjectDetailsQueryOptions({ projectId }),
  );

  if (!screener || !details) {
    return <LoadingIndicator />;
  }

  const existing = screener.existingResponse;
  if (
    !existing ||
    existing.status === undefined ||
    !STARTABLE_STATUSES.has(existing.status)
  ) {
    return <Received />;
  }

  return (
    <div className="mx-auto max-w-[var(--max-width-container)] px-4 pt-6 pb-18 md:px-8">
      <PreInviteScreen
        projectId={projectId}
        userId={externalUserId}
        project={details.project}
        projectData={{ projectStarted: existing.projectStarted === true }}
      />
    </div>
  );
}

/** Filed but not admitted: one neutral outcome, never a negative. */
function Received() {
  return (
    <div
      className="mx-auto max-w-lg px-4 py-16 text-center"
      data-testid="external-study-received"
    >
      <h1 className="text-display-xs font-semibold text-primary">
        <Trans>Thanks, we&apos;ve received your details</Trans>
      </h1>
      <p className="mt-3 text-md text-tertiary">
        <Trans>The researcher will be in touch. You can close this page.</Trans>
      </p>
    </div>
  );
}

function Unavailable() {
  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <h1 className="text-display-xs font-semibold text-primary">
        <Trans>This study is not available</Trans>
      </h1>
      <p className="mt-3 text-md text-tertiary">
        <Trans>
          Check the link you were sent, or ask the researcher for a new one.
        </Trans>
      </p>
    </div>
  );
}
